import { MapPin, Navigation, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GOOGLE_MAPS_URL = 'https://maps.app.goo.gl/XXLN9vxww1yf9Qaq7';
const WAZE_URL = 'https://www.waze.com/ul?q=DC%20Mall%20Damansara%20City&navigate=yes';
const EMBED_URL = 'https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=DC+Mall,Damansara+City,Kuala+Lumpur&zoom=16';

export const LocationCard = () => {
  const { t } = useTranslation();
  return (
    <section
      data-testid="location-section"
      className="py-16 lg:py-24 px-6 sm:px-8"
      style={{ backgroundColor: 'var(--vc-bg)' }}
    >
      <div className="max-w-[1160px] mx-auto">
        <div
          className="rounded-[24px] border overflow-hidden"
          style={{
            borderColor: 'rgba(255,255,255,0.08)',
            backgroundColor: 'rgba(255,255,255,0.03)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left: Embedded Google Map */}
            <div className="relative min-h-[260px] lg:min-h-[360px]">
              <iframe
                title="VERSE Clinic - DC Mall, Damansara City"
                src={EMBED_URL}
                className="absolute inset-0 w-full h-full"
                style={{
                  border: 0,
                  filter: 'saturate(0.3) brightness(0.7) contrast(1.1)',
                  pointerEvents: 'none',
                }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              {/* Dark overlay tint to match theme */}
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(7,21,37,0.4), rgba(7,21,37,0.2))',
                }}
              />
              {/* Bold gold pin overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex flex-col items-center">
                  {/* Outer pulse */}
                  <div className="relative">
                    <div
                      className="absolute rounded-full animate-ping"
                      style={{
                        backgroundColor: 'rgba(214,168,74,0.15)',
                        animationDuration: '2s',
                        width: '56px',
                        height: '56px',
                        top: '-8px',
                        left: '-8px',
                      }}
                    />
                    {/* Inner glow ring */}
                    <div
                      className="absolute rounded-full"
                      style={{
                        width: '48px',
                        height: '48px',
                        top: '-4px',
                        left: '-4px',
                        backgroundColor: 'rgba(214,168,74,0.1)',
                        border: '1px solid rgba(214,168,74,0.2)',
                      }}
                    />
                    {/* Pin circle */}
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center relative z-10"
                      style={{
                        backgroundColor: 'var(--vc-gold)',
                        border: '3px solid rgba(255,255,255,0.9)',
                        boxShadow: '0 0 24px rgba(214,168,74,0.5), 0 4px 12px rgba(0,0,0,0.4)',
                      }}
                    >
                      <MapPin className="w-5 h-5" style={{ color: 'var(--vc-ink)' }} />
                    </div>
                  </div>
                  {/* Pin tail shadow */}
                  <div
                    className="w-3 h-3 -mt-1 relative z-10"
                    style={{
                      backgroundColor: 'var(--vc-gold)',
                      clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    }}
                  />
                  {/* Ground shadow */}
                  <div
                    className="mt-1 w-8 h-2 rounded-full"
                    style={{
                      backgroundColor: 'rgba(0,0,0,0.3)',
                      filter: 'blur(3px)',
                    }}
                  />
                  {/* Label */}
                  <div
                    className="mt-2 px-3 py-1 rounded-lg"
                    style={{
                      backgroundColor: 'rgba(7,21,37,0.85)',
                      border: '1px solid rgba(214,168,74,0.3)',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    <p
                      className="text-[10px] font-bold uppercase tracking-[0.12em]"
                      style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}
                    >
                      DC Mall
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Info */}
            <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center items-center text-center">
              <p
                className="text-xs font-medium uppercase tracking-[0.15em] mb-3"
                style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}
              >
                {t('location.label')}
              </p>

              <h3
                className="text-xl sm:text-2xl font-bold tracking-tight"
                style={{ fontFamily: 'Inter, sans-serif', color: 'var(--vc-fg)' }}
              >
                {t('location.name')}
              </h3>

              <p
                className="mt-3 text-sm leading-relaxed"
                style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}
              >
                {t('location.city')}
              </p>

              <div
                className="mt-6 flex items-start gap-3"
                style={{ color: 'var(--vc-fg-subtle)' }}
              >
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--vc-gold)' }} />
                <p
                  className="text-sm leading-relaxed text-left"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('location.address_1')}<br />
                  {t('location.address_2')}
                </p>
              </div>

              <p
                className="mt-2 text-xs"
                style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}
              >
              </p>

              {/* Buttons: Google Maps + Waze */}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <a
                  data-testid="get-directions-google"
                  href={GOOGLE_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 h-11 px-5 text-sm font-medium rounded-xl transition-colors duration-200 active:scale-[0.98] no-underline"
                  style={{
                    backgroundColor: 'var(--vc-gold)',
                    color: 'var(--vc-ink)',
                    fontFamily: 'Inter, sans-serif',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--vc-gold-2)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--vc-gold)'}
                >
                  <Navigation className="w-4 h-4" />
                  {t('location.google')}
                </a>
                <a
                  data-testid="get-directions-waze"
                  href={WAZE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 h-11 px-5 text-sm font-medium rounded-xl border transition-colors duration-200 active:scale-[0.98] no-underline"
                  style={{
                    color: 'var(--vc-gold)',
                    borderColor: 'var(--vc-gold)',
                    backgroundColor: 'transparent',
                    fontFamily: 'Inter, sans-serif',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.12)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <ExternalLink className="w-4 h-4" />
                  {t('location.waze')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
